import { RadarChart } from "@mantine/charts";
import { Box } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { AxisKey } from "@utils/cognitiveProfile";

export interface CognitiveRadarAxis {
  key: AxisKey;
  score: number | null;
}

interface CognitiveRadarChartProps {
  axes: CognitiveRadarAxis[];
  height?: number;
}

interface TickProps {
  payload?: { value?: string };
  x?: number;
  y?: number;
  textAnchor?: "end" | "inherit" | "start" | "middle";
}

function WrappedAngleTick({ payload, x, y, textAnchor }: TickProps) {
  const text = payload?.value ?? "";
  const words = text.split(" ");
  const lines: string[] = [];
  const maxLineLength = 14;

  let current = "";
  for (const word of words) {
    if ((current + " " + word).trim().length <= maxLineLength) {
      current = (current + " " + word).trim();
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);

  return (
    <text
      x={x}
      y={y}
      textAnchor={textAnchor}
      fill="var(--oculera-text-secondary)"
      fontSize={11}
      fontWeight={500}
    >
      {lines.map((line, idx) => (
        <tspan key={`${line}-${idx}`} x={x} dy={idx === 0 ? 0 : 14}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

export function CognitiveRadarChart({ axes, height = 280 }: CognitiveRadarChartProps) {
  const { t } = useTranslation();

  const data = axes.map((a) => ({
    axisLabel: t(`cognitiveProfile.axes.${a.key}`),
    score: a.score,
  }));

  return (
    <Box>
      <RadarChart
        h={height}
        data={data}
        dataKey="axisLabel"
        withPolarGrid
        withPolarAngleAxis
        withPolarRadiusAxis={false}
        withTooltip
        radarChartProps={{ margin: { top: 16, right: 70, bottom: 16, left: 70 } }}
        polarAngleAxisProps={{ tick: WrappedAngleTick as never }}
        series={[
          {
            name: "score",
            color: "blue.6",
            opacity: 0.35,
            label: t("cognitiveProfile.chart.seriesLabel"),
          },
        ]}
      />
    </Box>
  );
}
